/**
 * PdfThumbnail — Lazy-loaded PDF first-page thumbnail using react-pdf.
 *
 * Code-split via React.lazy + Suspense so the ~300KB pdfjs worker only loads
 * when a PDF message becomes visible. Shows page count badge and click-to-open.
 */

import { Suspense, lazy, memo } from 'react';

interface PdfThumbnailProps {
  url: string;
  fileName?: string;
  fileSize?: number;
  isMine: boolean;
}

/** Shimmer placeholder matching compact document row height */
function PdfShimmer() {
  return (
    <div
      className="flex h-[4.5rem] items-center gap-3 rounded-lg border border-white/20 px-2"
      style={{
        background: 'linear-gradient(90deg, #ffffff 25%, #f1f5f9 50%, #ffffff 75%)',
        backgroundSize: '200% 100%',
        animation: 'progressive-image-shimmer 1.5s ease-in-out infinite',
      }}
    >
      <div className="h-14 w-11 flex-shrink-0 rounded bg-white/50" />
      <div className="flex-1 space-y-2">
        <div className="h-3 w-3/4 rounded bg-white/50" />
        <div className="h-2.5 w-1/2 rounded bg-white/50" />
      </div>
    </div>
  );
}

/** The actual renderer — code-split */
const PdfThumbnailInner = lazy(() => import('./PdfThumbnailInner'));

export const PdfThumbnail = memo(function PdfThumbnail(props: PdfThumbnailProps) {
  return (
    <Suspense fallback={<PdfShimmer />}>
      <PdfThumbnailInner {...props} />
    </Suspense>
  );
});

export default PdfThumbnail;
